/**
 * VehicleArchiveView - Vehicle Archive / Audit Trail View
 *
 * Displays archived vehicles organized by year with:
 * - Complete TCO (Total Cost of Ownership) breakdown
 * - Timeline of all events (maintenance, claims, fuel, fines, inspections)
 * - Document archive
 * - Presence duration and mileage range
 */

import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import { formatDate as fmtDate, formatNumber as fmtNumber } from '../../i18n/format.js';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../../api/core.js';
import {
  Archive,
  Calendar,
  Car,
  FileText,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Fuel,
  Wrench,
  AlertTriangle,
  ClipboardCheck,
  Receipt,
  Download,
  Eye,
  TrendingUp,
  Clock,
  MapPin,
  User,
  Filter,
  X,
  Shield,
  FileSignature,
  Folder,
  Package
} from 'lucide-react';
import { AccordionGroup, AccordionContainer } from '../common/AccordionGroup';
import { useAccordionState } from '../../hooks/useAccordionState.js';

const API_BASE = '/api/v1';

// Reason labels in French
const REASON_LABELS = {
  sale: 'Vente',
  lease_end: 'Fin de leasing',
  writeoff: 'Perte totale',
  theft: 'Vol',
  scrappage: 'Mise a la casse',
  transfer: 'Transfert',
  other: 'Autre'
};

// Event type labels and icons
const EVENT_CONFIG = {
  maintenance: { label: 'Maintenance', icon: Wrench, color: 'text-blue-600 bg-blue-50' },
  claim: { label: 'Sinistre', icon: AlertTriangle, color: 'text-red-600 bg-red-50' },
  fuel: { label: 'Carburant', icon: Fuel, color: 'text-green-600 bg-green-50' },
  inspection: { label: 'Controle', icon: ClipboardCheck, color: 'text-purple-600 bg-purple-50' },
  fine: { label: 'Amende', icon: Receipt, color: 'text-orange-600 bg-orange-50' },
  document: { label: 'Document', icon: FileText, color: 'text-gray-600 bg-gray-50' },
  insurance: { label: 'Assurance', icon: DollarSign, color: 'text-indigo-600 bg-indigo-50' }
};

// Document category configuration for accordion display
const DOCUMENT_CATEGORIES = {
  registration: { label: 'Carte Grise & Immatriculation', icon: FileText, color: 'blue' },
  technical: { label: 'Contrôle Technique', icon: ClipboardCheck, color: 'purple' },
  insurance: { label: 'Assurance', icon: Shield, color: 'emerald' },
  leasing: { label: 'Contrats (Leasing/Location)', icon: FileSignature, color: 'amber' },
  maintenance: { label: 'Factures Maintenance', icon: Wrench, color: 'blue' },
  claims: { label: 'Dossiers Sinistres', icon: AlertTriangle, color: 'red' },
  misc: { label: 'Autres Documents', icon: Folder, color: 'gray' }
};



// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return fmtDate(dateStr);
};

// Format number with separators
const formatNumber = (num) => {
  if (num == null) return '-';
  return fmtNumber(num);
};

export default function VehicleArchiveView({ embedded = false }) {
  // Argent : un seul outil. `formatCurrency` prend la ligne (archive, vehicule) pour
  // en deduire la filiale ; sans ligne, la devise du perimetre.
  const { money } = useOrgCurrency();
  const formatCurrency = (amount, row) => (amount == null ? '-' : money(amount, row));
  const [archives, setArchives] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [tcoReport, setTcoReport] = useState(null);
  const [annualStats, setAnnualStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedYears, setExpandedYears] = useState({});
  // Document accordion state - DB-backed, closed by default
  const [groupedDocuments, setGroupedDocuments] = useState({});
  const [docCounts, setDocCounts] = useState({});
  const [exportLoading, setExportLoading] = useState(false);

  // DB-backed accordion state for document categories (closed by default)
  const {
    isExpanded: isDocCategoryExpanded,
    toggleGroup: toggleDocCategoryAccordion,
  } = useAccordionState({ viewCode: 'vehicle_archive_documents' });

  // Fetch available years
  const fetchYears = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/vehicle-archives/years`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.ok) {
        setYears(data.data);
        // Expand most recent year by default
        if (data.data.length > 0) {
          const recentYear = data.data[0].exit_year;
          setExpandedYears({ [recentYear]: true });
          setSelectedYear(recentYear);
        }
      }
    } catch (err) {
      console.error('Error fetching years:', err);
      setError('Erreur lors du chargement des annees');
    }
  }, []);

  // Fetch archives for a year
  const fetchArchives = useCallback(async (year) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/vehicle-archives?year=${year}`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.ok) {
        setArchives(data.data);
      }
    } catch (err) {
      console.error('Error fetching archives:', err);
      setError('Erreur lors du chargement des archives');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch archive detail with events
  const fetchArchiveDetail = useCallback(async (archiveId) => {
    try {
      setDetailLoading(true);

      // Fetch archive and TCO report in parallel
      const [archiveRes, tcoRes] = await Promise.all([
        fetch(`${API_BASE}/vehicle-archives/${archiveId}`, {
          headers: getAuthHeaders()
        }),
        fetch(`${API_BASE}/vehicle-archives/${archiveId}/tco-report`, {
          headers: getAuthHeaders()
        })
      ]);

      const archiveData = await archiveRes.json();
      const tcoData = await tcoRes.json();

      if (archiveData.ok) {
        setSelectedArchive(archiveData.data);
      }
      if (tcoData.ok) {
        setTcoReport(tcoData.data);
      }
    } catch (err) {
      console.error('Error fetching archive detail:', err);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Fetch annual TCO stats
  const fetchAnnualStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/vehicle-archives/reports/annual-tco`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.ok) {
        setAnnualStats(data.data);
      }
    } catch (err) {
      console.error('Error fetching annual stats:', err);
    }
  }, []);

  // Fetch grouped documents for archive
  const fetchGroupedDocuments = useCallback(async (archiveId) => {
    try {
      const res = await fetch(`${API_BASE}/vehicle-archives/${archiveId}/documents`, {
        headers: getAuthHeaders()
      });
      const data = await res.json();
      if (data.ok) {
        setGroupedDocuments(data.grouped || {});
        setDocCounts(data.counts || {});
        // Accordion state is managed by useAccordionState hook (closed by default, persisted to DB)
      }
    } catch (err) {
      console.error('Error fetching grouped documents:', err);
    }
  }, []);

  // Download archive pack (ZIP)
  const downloadArchivePack = async (archiveId, licensePlate) => {
    try {
      setExportLoading(true);
      const res = await fetch(`${API_BASE}/vehicle-archives/${archiveId}/export-pack`, {
        headers: getAuthHeaders()
      });

      if (!res.ok) {
        throw new Error('Erreur lors du telechargement');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ARCHIVE_${(licensePlate || archiveId).replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading archive:', err);
      alert('Erreur lors du téléchargement de l\'archive');
    } finally {
      setExportLoading(false);
    }
  };

  // Toggle document category accordion (DB-backed)
  const toggleDocCategory = (category) => {
    toggleDocCategoryAccordion(`doc:${category}`);
  };

  useEffect(() => {
    fetchYears();
    fetchAnnualStats();
  }, [fetchYears, fetchAnnualStats]);

  // Fetch grouped documents when archive is selected
  useEffect(() => {
    if (selectedArchive?.id) {
      fetchGroupedDocuments(selectedArchive.id);
    } else {
      setGroupedDocuments({});
      setDocCounts({});
      // Accordion state is automatically managed by useAccordionState hook
    }
  }, [selectedArchive?.id, fetchGroupedDocuments]);

  useEffect(() => {
    if (selectedYear) {
      fetchArchives(selectedYear);
    }
  }, [selectedYear, fetchArchives]);

  // Toggle year expansion
  const toggleYear = (year) => {
    setExpandedYears(prev => ({ ...prev, [year]: !prev[year] }));
    if (!expandedYears[year]) {
      setSelectedYear(year);
    }
  };

  // Close detail panel
  const closeDetail = () => {
    setSelectedArchive(null);
    setTcoReport(null);
  };

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50'}>
      {/* Header - Only show if not embedded */}
      {!embedded && (
        <div className="bg-white border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <Archive className="w-6 h-6 text-slate-600" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">Archives Véhicules</h1>
              <p className="text-sm text-gray-500">Historique complet et TCO des véhicules sortis de la flotte</p>
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Left Panel - Year Navigation & List */}
        <div className={`${selectedArchive ? 'w-1/2' : 'w-full'} border-r bg-white transition-all`}>
          {/* Annual Summary Cards */}
          {annualStats.length > 0 && (
            <div className="p-4 border-b bg-slate-50">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Resume annuel TCO</h3>
              <div className="grid grid-cols-3 gap-3">
                {annualStats.slice(0, 3).map(stat => (
                  <div key={stat.exit_year} className="bg-white rounded-lg border p-3">
                    <div className="text-lg font-bold text-gray-900">{stat.exit_year}</div>
                    <div className="text-sm text-gray-500">{stat.vehicle_count} véhicule(s)</div>
                    <div className="text-sm font-medium text-blue-600">{formatCurrency(stat.total_tco)}</div>
                    <div className="text-xs text-gray-400">{formatNumber(stat.total_km)} km</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Year Accordion */}
          <div className="divide-y">
            {years.map(yearData => (
              <div key={yearData.exit_year}>
                {/* Year Header */}
                <button
                  onClick={() => toggleYear(yearData.exit_year)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {expandedYears[yearData.exit_year] ? (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                    <Calendar className="w-5 h-5 text-slate-500" />
                    <span className="font-medium text-gray-900">{yearData.exit_year}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="bg-slate-100 px-2 py-0.5 rounded text-gray-600">
                      {yearData.count} véhicule(s)
                    </span>
                    <span className="text-blue-600 font-medium">
                      {formatCurrency(yearData.total_tco)}
                    </span>
                  </div>
                </button>

                {/* Year Content */}
                {expandedYears[yearData.exit_year] && (
                  <div className="bg-gray-50 px-4 pb-4">
                    {loading ? (
                      <div className="text-center py-8 text-gray-500">Chargement...</div>
                    ) : archives.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">Aucun véhicule archivé pour cette année</div>
                    ) : (
                      <div className="space-y-2">
                        {archives.map(archive => (
                          <div
                            key={archive.id}
                            onClick={() => fetchArchiveDetail(archive.id)}
                            className={`bg-white rounded-lg border p-4 cursor-pointer transition-all hover:border-blue-300 hover:shadow-sm ${
                              selectedArchive?.id === archive.id ? 'border-blue-500 ring-1 ring-blue-200' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                <Car className="w-8 h-8 text-slate-400" />
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {archive.make} {archive.model}
                                  </div>
                                  <div className="text-sm text-gray-500">{archive.license_plate}</div>
                                </div>
                              </div>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                archive.exit_reason === 'sale' ? 'bg-green-100 text-green-700' :
                                archive.exit_reason === 'writeoff' ? 'bg-red-100 text-red-700' :
                                archive.exit_reason === 'lease_end' ? 'bg-blue-100 text-blue-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {REASON_LABELS[archive.exit_reason] || archive.exit_reason}
                              </span>
                            </div>

                            <div className="mt-3 grid grid-cols-4 gap-4 text-sm">
                              <div>
                                <div className="text-gray-400 text-xs">Presence</div>
                                <div className="font-medium">{archive.presence_months} mois</div>
                              </div>
                              <div>
                                <div className="text-gray-400 text-xs">Kilométrage</div>
                                <div className="font-medium">{formatNumber(archive.total_km)} km</div>
                              </div>
                              <div>
                                <div className="text-gray-400 text-xs">TCO Total</div>
                                <div className="font-medium text-blue-600">{formatCurrency(archive.tco_total, archive)}</div>
                              </div>
                              <div>
                                <div className="text-gray-400 text-xs">TCO/km</div>
                                <div className="font-medium">{archive.tco_per_km?.toFixed(2)} EUR</div>
                              </div>
                            </div>

                            <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                              <span>Sortie: {formatDate(archive.disposal_date)}</span>
                              {archive.last_driver_name && (
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {archive.last_driver_name}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {years.length === 0 && !loading && (
            <div className="p-8 text-center text-gray-500">
              <Archive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aucun véhicule archivé</p>
              <p className="text-sm mt-1">Les véhicules sortis de la flotte apparaîtront ici</p>
            </div>
          )}
        </div>

        {/* Right Panel - Archive Detail */}
        {selectedArchive && (
          <div className="w-1/2 bg-white overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
            {/* Detail Header */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  {selectedArchive.make} {selectedArchive.model} ({selectedArchive.year || '-'})
                </h2>
                <p className="text-sm text-gray-500">
                  {selectedArchive.license_plate} - {selectedArchive.vin || 'VIN non renseigne'}
                </p>
              </div>
              <button
                onClick={closeDetail}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {detailLoading ? (
              <div className="p-8 text-center text-gray-500">Chargement du dossier...</div>
            ) : (
              <div className="p-6 space-y-6">
                {/* Presence Section */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Presence dans la flotte
                  </h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-gray-500">Acquisition</div>
                      <div className="font-medium">{formatDate(selectedArchive.acquisition_date)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Sortie</div>
                      <div className="font-medium">{formatDate(selectedArchive.disposal_date)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Duree</div>
                      <div className="font-medium">{selectedArchive.presence_months} mois</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Km entree</div>
                      <div className="font-medium">{formatNumber(selectedArchive.entry_mileage)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Km sortie</div>
                      <div className="font-medium">{formatNumber(selectedArchive.exit_mileage)}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Total parcouru</div>
                      <div className="font-medium text-blue-600">{formatNumber(selectedArchive.total_km)} km</div>
                    </div>
                  </div>
                </div>

                {/* TCO Section */}
                {tcoReport && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-blue-50 px-4 py-3 flex items-center justify-between">
                      <h3 className="font-medium text-blue-900 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        TCO - Cout Total de Possession
                      </h3>
                      <div className="text-2xl font-bold text-blue-700">
                        {formatCurrency(tcoReport.tco?.totals?.gross_tco, selectedArchive)}
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-4">
                        {/* Cost Breakdown */}
                        {Object.entries(tcoReport.tco?.breakdown || {}).map(([key, item]) => (
                          <div key={key} className="flex items-center justify-between py-2 border-b">
                            <span className="text-gray-600">{item.label}</span>
                            <span className="font-medium">{formatCurrency(item.total || item.total_payments || 0, selectedArchive)}</span>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4 pt-4 border-t grid grid-cols-3 gap-4 bg-gray-50 -mx-4 -mb-4 p-4">
                        <div className="text-center">
                          <div className="text-xs text-gray-500">TCO / mois</div>
                          <div className="text-lg font-bold text-gray-900">
                            {formatCurrency(tcoReport.tco?.totals?.per_month, selectedArchive)}
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">TCO / km</div>
                          <div className="text-lg font-bold text-gray-900">
                            {tcoReport.tco?.totals?.per_km?.toFixed(3)} EUR
                          </div>
                        </div>
                        <div className="text-center">
                          <div className="text-xs text-gray-500">Valeur recuperee</div>
                          <div className="text-lg font-bold text-green-600">
                            {formatCurrency(tcoReport.tco?.totals?.disposal_recovery, selectedArchive)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Event Timeline */}
                <div>
                  <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Historique des evenements ({selectedArchive.events?.length || 0})
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {(selectedArchive.events || []).map((event, idx) => {
                      const config = EVENT_CONFIG[event.event_type] || EVENT_CONFIG.document;
                      const Icon = config.icon;
                      return (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className={`p-2 rounded-lg ${config.color}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900 truncate">{event.title}</span>
                              <span className="text-sm text-gray-500">{formatDate(event.event_date)}</span>
                            </div>
                            {event.description && (
                              <p className="text-sm text-gray-500 truncate">{event.description}</p>
                            )}
                            {event.amount > 0 && (
                              <span className="text-sm font-medium text-blue-600">{formatCurrency(event.amount, selectedArchive)}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                    {(!selectedArchive.events || selectedArchive.events.length === 0) && (
                      <p className="text-gray-500 text-sm py-4 text-center">Aucun evenement archive</p>
                    )}
                  </div>
                </div>

                {/* Documents - Accordion Display */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-gray-900 flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Documents archives ({docCounts.total || selectedArchive.documents?.length || 0})
                    </h3>
                    <button
                      onClick={() => downloadArchivePack(selectedArchive.id, selectedArchive.license_plate)}
                      disabled={exportLoading}
                      className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {exportLoading ? (
                        <>
                          <span className="animate-spin">⏳</span>
                          Preparation...
                        </>
                      ) : (
                        <>
                          <Package className="w-4 h-4" />
                          Telecharger l'archive (.zip)
                        </>
                      )}
                    </button>
                  </div>

                  <AccordionContainer>
                    {Object.entries(DOCUMENT_CATEGORIES).map(([category, config]) => {
                      const docs = groupedDocuments[category] || [];
                      const count = docCounts[category] || docs.length;
                      const CategoryIcon = config.icon;

                      return (
                        <AccordionGroup
                          key={category}
                          id={category}
                          title={config.label}
                          count={count}
                          icon={CategoryIcon}
                          color={config.color}
                          isExpanded={isDocCategoryExpanded(`doc:${category}`)}
                          onToggle={() => toggleDocCategory(category)}
                          emptyMessage="Aucun document dans cette categorie"
                        >
                          <div className="space-y-2">
                            {docs.map((doc, idx) => (
                              <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                                <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-700 truncate">
                                    {doc.document_name || doc.original_filename || 'Document'}
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {doc.document_type} {doc.document_date && `- ${formatDate(doc.document_date)}`}
                                  </div>
                                </div>
                                {doc.file_path && (
                                  <button
                                    className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                                    title="Voir le document"
                                  >
                                    <Eye className="w-4 h-4 text-gray-500" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </AccordionGroup>
                      );
                    })}
                  </AccordionContainer>

                  {docCounts.total === 0 && (!selectedArchive.documents || selectedArchive.documents.length === 0) && (
                    <p className="text-gray-500 text-sm py-4 text-center">Aucun document archive</p>
                  )}
                </div>

                {/* Exit Details */}
                <div className="bg-orange-50 rounded-lg p-4">
                  <h3 className="font-medium text-orange-900 mb-3">Détails de la sortie</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-orange-700">Motif</div>
                      <div className="font-medium">{REASON_LABELS[selectedArchive.exit_reason] || selectedArchive.exit_reason}</div>
                    </div>
                    {selectedArchive.exit_reason_detail && (
                      <div>
                        <div className="text-orange-700">Detail</div>
                        <div className="font-medium">{selectedArchive.exit_reason_detail}</div>
                      </div>
                    )}
                    {selectedArchive.sale_price > 0 && (
                      <div>
                        <div className="text-orange-700">Prix de vente / Indemnite</div>
                        <div className="font-medium">{formatCurrency(selectedArchive.sale_price, selectedArchive)}</div>
                      </div>
                    )}
                    <div>
                      <div className="text-orange-700">Archive le</div>
                      <div className="font-medium">{formatDate(selectedArchive.archived_at)}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
